import { Box } from '@mui/material';
import React, { useState, useCallback, useEffect } from 'react';

import { ServiceFilterModalProps, FilterState } from './ServiceFilterModal.props';
import {
  FilterSection,
  FilterSectionTitle,
  FilterOptionsGrid,
  FilterOption,
  ApplyButton,
} from './ServiceFilterModal.styles';
import { BottomSheet } from '../../../../../components/molecules/BottomSheet';
import { COLORS } from '../../../../../configs/colors';
import { ServiceFilter } from '../ServiceListFilters/ServiceListFilters.props';

export const ServiceFilterModal: React.FC<ServiceFilterModalProps> = ({
  open,
  onClose,
  currentFilters,
  onApply,
  onClearAll,
}) => {
  const [localFilters, setLocalFilters] = useState<FilterState>(currentFilters);

  useEffect(() => {
    if (open) setLocalFilters(currentFilters);
  }, [open, currentFilters]);

  const updateFilter = useCallback(<K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleApply = useCallback(() => {
    onApply(localFilters);
    onClose();
  }, [localFilters, onApply, onClose]);

  const handleClearAll = useCallback(() => {
    onClearAll();
    onClose();
  }, [onClearAll, onClose]);

  const serviceTypes: Array<{ value: ServiceFilter; label: string }> = [
    { value: 'all', label: 'All Services' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'installOrDecom', label: 'Install/Decom' },
  ];

  const dateRanges: Array<{ value: string; label: string }> = [
    { value: 'open', label: 'Open Jobs' },
    { value: 'closed7Days', label: 'Closed 7 Days' },
    { value: 'closed30Days', label: 'Closed 30 Days' },
  ];

  const footer = (
    <Box sx={{ width: '100%' }}>
      <ApplyButton onClick={handleApply}>Apply Filters</ApplyButton>
    </Box>
  );

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      height="large"
      title="Filters"
      subtitle="Narrow your service list"
      footer={footer}
    >
      <Box sx={{ padding: '0 16px' }}>
        <FilterSection>
          <FilterSectionTitle>Service Type</FilterSectionTitle>
          <FilterOptionsGrid>
            {serviceTypes.map((st) => (
              <FilterOption
                key={st.value}
                active={localFilters.serviceType === st.value}
                onClick={() => updateFilter('serviceType', st.value)}
              >
                {st.label}
              </FilterOption>
            ))}
          </FilterOptionsGrid>
        </FilterSection>

        <FilterSection>
          <FilterSectionTitle>Date Range</FilterSectionTitle>
          <FilterOptionsGrid>
            {dateRanges.map((dr) => (
              <FilterOption
                key={dr.value}
                active={localFilters.dateRange === dr.value}
                onClick={() => updateFilter('dateRange', dr.value)}
              >
                {dr.label}
              </FilterOption>
            ))}
          </FilterOptionsGrid>
        </FilterSection>

        <FilterSection>
          <FilterSectionTitle>Quick Filters</FilterSectionTitle>
          <FilterOptionsGrid>
            <FilterOption
              active={localFilters.urgent}
              onClick={() => updateFilter('urgent', !localFilters.urgent)}
            >
              Urgent
            </FilterOption>
            <FilterOption
              active={localFilters.today}
              onClick={() => updateFilter('today', !localFilters.today)}
            >
              Today
            </FilterOption>
          </FilterOptionsGrid>
        </FilterSection>

        <Box sx={{ textAlign: 'center', paddingBottom: '8px' }} onClick={handleClearAll}>
          <Box
            component="button"
            sx={{
              background: 'transparent',
              border: 'none',
              color: COLORS.NavyBlue,
              fontWeight: 600,
              fontSize: '0.85rem',
              cursor: 'pointer',
              padding: '6px 12px',
              fontFamily: 'inherit',
              '&:active': { opacity: 0.6 },
            }}
          >
            Clear All
          </Box>
        </Box>
      </Box>
    </BottomSheet>
  );
};
